import { Grid, Box, Button, Modal, makeStyles, Checkbox } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { v4 as uuid } from 'uuid';
import QCXFocusLock from '../../../../../../components/focus-lock/QCXFocusLock';
import QCXFormSubtitle from '../../../../../../components/form-title/QCXFormSubtitle';
import QCXErrorAlert from '../../../../../../shared-components/alert/QCXErrorAlert';
import QCXDataGrid from '../../../../../../shared-components/data-grid/QCXDataGrid';
import QCXSelectClienteAutocomplete from '../../../../../../shared-components/final-select-cliente-field/QCXSelectClienteAutocomplete';
import QCXFinalTextField from '../../../../../../shared-components/final-text-field/QCXFinalTextField';
import QCXForm from '../../../../../../shared-components/form/QCXForm';
import QCXSelectDespachanteAutocomplete from '../../../../../../shared-components/select-despachante/QCXSelectDespachanteAutocomplete';
import QCXSelectPaisAutocomplete from '../../../../../../shared-components/select-pais/QCXSelectPaisAutocomplete';
import QCXFinalCheckboxField from '../../../../../../shared-components/final-checkbox-field/QCXFinalCheckboxField';
import { paisActions } from '../../../../../../features/pais/paisSlice';
import { AUTO_LOAD_CONTROL } from '../../../../../../features/config-control';
import {
  setErrorFeedback,
  setPreformattedFeedback,
  setSuccessFeedback,
} from '../../../../../../features/feedback/feedbackSlice';
import { doPostQCXRequest } from '../../../../../../utils/api/domain/domain-api-utils';
import { loading, success } from '../../../../../../features/empresa-estrangeira/empresaEstrangeiraSlice';

const useStyles = makeStyles({
  modalContent: {
    width: '90%',
    maxWidth: 1400,
    maxHeight: '80vh',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    justifyContent: 'flex-start',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    overflowY: 'scroll',
  },
  uploadButton: {
    display: 'inline',
    background: 'none',
    border: 'none',
    padding: '0px',
    fontWeight: '600',
    textDecoration: 'underline',
    color: '#0000FF',
    cursor: 'pointer',
  },
  submitButtonsBox: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  uploadSubmitBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  avisoVinculosButton: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
  },
  innerModal: {
    zIndex: 999999,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  innerModalContent: {
    maxWidth: 800,
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: 4,
  },
  avisoVinculosTextArea: {
    width: '100%',
  },
});

export default function OperadoresEstrangeirosRecuperarPorCnpjModal({
  isModalOpen,
  closeModal,
  refetchOperadoresEstrangeiros,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { keycloak } = useKeycloak();
  keycloak.updateToken(10);

  const [clienteId, setClienteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showOperadoresCandidates, setShowOperadoresCandidates] = useState(false);
  const [listaOperadoresCandidates, setListaOperadoresCandidates] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const selectedAmount = useMemo(() => Object.keys(selectedMap).length, [selectedMap]);
  const allChecked = selectedAmount === listaOperadoresCandidates.length;

  useEffect(() => {
    if (!isModalOpen) {
      setSubmitting(false);
      setShowOperadoresCandidates(false);
      setListaOperadoresCandidates([]);
      setSelectedMap({});
    }
  }, [isModalOpen]);

  useEffect(() => {
    dispatch(paisActions.changeControlTo(AUTO_LOAD_CONTROL));
  }, []);

  const handleRecuperarCheckboxChange = (evt, key) => {
    setSelectedMap((prevMap) => {
      if (evt.target.checked) prevMap[key] = true;
      else delete prevMap[key];

      return { ...prevMap };
    });
  };

  const handleSelecionarTodos = () => {
    if (allChecked) {
      setSelectedMap({});
    } else {
      const newSelectedMap = {};
      listaOperadoresCandidates.forEach((p) => {
        newSelectedMap[p.id] = true;
      });
      setSelectedMap(newSelectedMap);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'recuperar',
        headerName: 'Recuperar',
        width: 130,
        renderCell: ({ row }) => (
          <Checkbox
            key={String(selectedMap[row.id])}
            checked={selectedMap[row.id]}
            onChange={(evt) => handleRecuperarCheckboxChange(evt, row.id)}
          />
        ),
      },
      {
        field: 'seq',
        headerName: 'Seq.',
        width: 90,
        valueGetter: ({ row }) => row?.seq ?? '-',
      },
      {
        field: 'nome',
        headerName: 'Nome',
        flex: 2,
        valueGetter: ({ row }) => row?.nome ?? '-',
      },
      {
        field: 'codigo',
        headerName: 'Código',
        flex: 1,
        valueGetter: ({ row }) => row?.codigo ?? '-',
      },
      {
        field: 'versao',
        headerName: 'Versão',
        flex: 1,
        valueGetter: ({ row }) => row?.versao ?? '-',
      },
      {
        field: 'codigoInterno',
        headerName: 'Código interno',
        flex: 1,
        valueGetter: ({ row }) => row?.codigoInterno ?? '-',
      },
      {
        field: 'pais',
        headerName: 'País',
        flex: 1,
        valueGetter: ({ row }) => row?.codigoPais ?? '-',
      },
      {
        field: 'cidade',
        headerName: 'Cidade',
        flex: 1,
        valueGetter: ({ row }) => row?.nomeCidade ?? '-',
      },
      {
        field: 'logradouro',
        headerName: 'Logradouro',
        flex: 2,
        valueGetter: ({ row }) => row?.logradouro ?? '-',
      },
    ],
    [selectedMap]
  );

  const handleConfirm = async (data) => {
    setSubmitting(true);
    setShowOperadoresCandidates(true);
    setListaOperadoresCandidates([]);

    const body = {
      clienteId: data.cliente,
      despachanteId: data.despachante,
    };
    setClienteId(data.cliente);

    if (data.codigo) body.codigo = data.codigo;
    if (data.codigoInterno) body.codigoInterno = data.codigoInterno;
    if (data.nome) body.nome = data.nome;
    if (data.numeroIdentificacao) body.nome = data.numeroIdentificacao;
    if (data.exibirDesativados) body.exibirDesativados = data.exibirDesativados;
    if (data?.pais?.id && data?.pais?.id > 0) body.paisId = data.pais.id;

    try {
      const response = await doPostQCXRequest(
        ['exportadorfabricante', 'list-recuperacao-operadores-estrangeiros-candidates'],
        body
      );

      if (response.status === 200) {
        response.data?.forEach?.((p) => {
          p.id = uuid();
        });
        const candidates = response.data ?? [];
        candidates.sort((a, b) => a.seq - b.seq);
        setListaOperadoresCandidates(candidates);
      }
    } catch (error) {
      console.log(error);
      let errorMessage = 'Erro ao consultar operadores estrangeiros do portal único';
      if (error?.response?.data?.hasError && error?.response?.data?.message) {
        errorMessage = error?.response?.data?.message;
      }

      dispatch(
        setErrorFeedback({
          message: errorMessage,
        })
      );
    } finally {
      setSubmitting(false);
      setShowOperadoresCandidates(true);
    }
  };

  const submitSelected = useCallback(async () => {
    try {
      dispatch(loading());

      const body = listaOperadoresCandidates.filter((p) => !!selectedMap[p.id]);
      const queryParams = [{ name: 'clienteId', value: clienteId }];
      const response = await doPostQCXRequest(
        ['exportadorfabricante', 'integrar-recuperacao-operadores-estrangeiros-candidates'],
        body,
        queryParams
      );

      if (response.status > 199 && response.status < 300) {
        const retornoMensagens = response.data;
        if (Array.isArray(retornoMensagens) && retornoMensagens.length > 0) {
          dispatch(
            setPreformattedFeedback({
              message: response.data.join('\n\n'),
            })
          );
        } else {
          dispatch(
            setSuccessFeedback({
              message: 'Operadores estrangeiros recuperados com sucesso.',
            })
          );
        }
        closeModal();
        refetchOperadoresEstrangeiros?.();
      } else {
        throw new Error('Status inesperado.');
      }
    } catch (error) {
      console.log(error);
      let errorMessage = 'Erro ao recuperar operadores estrangeiros';
      if (typeof error?.response?.data === 'string') {
        errorMessage = error?.response?.data;
      }

      dispatch(
        setErrorFeedback({
          message: errorMessage,
        })
      );
    } finally {
      dispatch(success());
    }
  }, [listaOperadoresCandidates, selectedMap, clienteId, closeModal, refetchOperadoresEstrangeiros]);

  return (
    <Modal open={isModalOpen} onClose={closeModal}>
      <QCXFocusLock style={{ width: '100%' }}>
        <div className={classes.modalContent}>
          <Box width="100%">
            <h2>Recuperar operadores estrangeiros do portal único</h2>

            <Form onSubmit={handleConfirm} initialValues={{ situacao: '0' }}>
              {({ values, handleSubmit }) => (
                <QCXForm onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <QCXSelectClienteAutocomplete id="cliente" name="cliente" label="Cliente" required />
                    </Grid>
                    <Grid item xs={6}>
                      <QCXSelectDespachanteAutocomplete
                        label="Despachante"
                        name="despachante"
                        required
                        disabled={!values?.cliente}
                        clienteId={values?.cliente}
                        useFirstAsDefault={!!values?.cliente}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <QCXSelectPaisAutocomplete
                        autoLoad
                        id="pais"
                        name="pais.id"
                        label="País"
                        disabled={!values?.cliente}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <QCXFinalCheckboxField
                        id="exibirDesativados"
                        key="exibirDesativados"
                        name="exibirDesativados"
                        label="Exibir registros desativados"
                        disabled={!values?.cliente}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <QCXFinalTextField
                        id="numeroIdentificacao"
                        name="numeroIdentificacao"
                        label="Número de identificação (TIN)"
                        maxLength={35}
                        disabled={!values?.cliente}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <QCXFinalTextField
                        id="nome"
                        name="nome"
                        label="Nome"
                        maxLength={70}
                        disabled={!values?.cliente}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <QCXFinalTextField
                        id="codigo"
                        key="codigo"
                        name="codigo"
                        label="Código"
                        maxLength={35}
                        disabled={!values?.cliente}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <QCXFinalTextField
                        id="codigoInterno"
                        key="codigoInterno"
                        name="codigoInterno"
                        label="Código interno"
                        maxLength={35}
                        disabled={!values?.cliente}
                      />
                    </Grid>
                  </Grid>

                  <Box className={classes.submitButtonsBox}>
                    <Button variant="outlined" color="default" onClick={closeModal}>
                      Fechar
                    </Button>
                    <Button disabled={submitting} type="submit" variant="contained" color="secondary">
                      Confirmar
                    </Button>
                  </Box>
                </QCXForm>
              )}
            </Form>
          </Box>

          {showOperadoresCandidates ? (
            <Grid container spacing={2} style={{ marginTop: 24 }}>
              <Grid item xs={12}>
                <QCXFormSubtitle title="Operadores para recuperar" />
              </Grid>

              <Grid item xs={12}>
                {listaOperadoresCandidates.length > 500 ? (
                  <QCXErrorAlert>
                    A consulta retornou mais de 500 operadores estrangeiros. Para ver o restante, reveja os filtros
                    aplicados.
                  </QCXErrorAlert>
                ) : null}
              </Grid>

              <Grid item xs={12}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={selectedAmount > 0 && allChecked}
                      onChange={handleSelecionarTodos}
                      disabled={submitting || listaOperadoresCandidates.length === 0}
                    />
                    <p style={{ lineHeight: 0 }}>Selecionar todos</p>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <QCXDataGrid loading={submitting} columns={columns} rows={listaOperadoresCandidates} />
              </Grid>
              <Grid item xs={12}>
                <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <span>{selectedAmount} produto(s) selecionado(s) para recuperação.</span>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box className={classes.uploadSubmitBox}>
                  <Button
                    disabled={submitting || selectedAmount === 0}
                    onClick={submitSelected}
                    variant="contained"
                    color="secondary"
                  >
                    Integrar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </div>
      </QCXFocusLock>
    </Modal>
  );
}
